import type { KayaState } from "./types";

/**
 * The period-day vocabulary (ADR-033 decision 6).
 *
 * Every answer is a key on the day's existing tombstoned `symptoms` set rather than a new DayLog
 * field: a scalar per day would need its own merge rule, while a key such as `pain:2` rides the
 * set the merge already resolves (ADR-019). A single-choice group is a family of keys sharing a
 * prefix; writing one tombstones its siblings (`store/toggles.ts::setSymptomChoice`).
 *
 * The four keys that existed before keep their names and meanings (invariant 3): `cramps`,
 * `lowenergy`, `heavy`, `fine`. The new keys imply them — see `implies`.
 */

export type ChoiceGroupId = "flow" | "pain" | "breast" | "energy" | "mood" | "sleep";

export interface SymptomOption {
  value: string;
  label: string;
  /** the stored key, `<group>:<value>` */
  key: string;
}

export interface ChoiceGroup {
  id: ChoiceGroupId;
  label: string;
  options: SymptomOption[];
  /** asked on any day, not only period days */
  anyDay?: boolean;
}

export interface ToggleSymptom {
  key: string;
  label: string;
  /** shown only while this key is chosen */
  requires?: string;
}

export const SYMPTOM_FINE = "fine";
/** `tender` was a toggle until breast pain became a graded group (ADR-060): every old day still
 *  holds it, and `breast:1..3` writes it alongside, as `pain:2` writes `cramps`. */
export const LEGACY_KEYS = ["cramps", "lowenergy", "heavy", "fine", "tender"] as const;

function group(id: ChoiceGroupId, label: string, opts: [string, string][], anyDay?: boolean): ChoiceGroup {
  return {
    id,
    label,
    options: opts.map(([value, l]) => ({ value, label: l, key: `${id}:${value}` })),
    ...(anyDay ? { anyDay } : {}),
  };
}

/** In the order the sheet asks them. */
export const CHOICE_GROUPS: ChoiceGroup[] = [
  group("flow", "Flow", [
    ["spotting", "spotting"],
    ["light", "light"],
    ["medium", "medium"],
    ["heavy", "heavy"],
  ]),
  group("pain", "Pain", [
    ["0", "none"],
    ["1", "mild"],
    ["2", "moderate"],
    ["3", "severe"],
  ]),
  group(
    "breast",
    "Breast pain",
    [
      ["0", "none"],
      ["1", "mild"],
      ["2", "moderate"],
      ["3", "severe"],
    ],
    true
  ),
  group(
    "energy",
    "Energy",
    [
      ["0", "low"],
      ["1", "okay"],
      ["2", "good"],
    ],
    true
  ),
  group("mood", "Mood", [
    ["steady", "steady"],
    ["low", "low"],
    ["irritable", "irritable"],
    ["anxious", "anxious"],
  ]),
  group(
    "sleep",
    "Sleep",
    [
      ["0", "poor"],
      ["1", "okay"],
      ["2", "good"],
    ],
    true
  ),
];

export const SOAKED = "soaked";

/** "Feel · any that apply", in the sheet's order, plus the heavy-flow marker (ADR-060). */
export const TOGGLES: ToggleSymptom[] = [
  { key: "bloating", label: "bloating" },
  { key: "cravings:sweet", label: "cravings sweet" },
  { key: "cravings:salty", label: "cravings salty" },
  { key: "headache", label: "headache" },
  { key: "backache", label: "back ache" },
  { key: "nausea", label: "nausea" },
  { key: "skin", label: "spots" },
  { key: "constipation", label: "constipation" },
  { key: "loose", label: "loose stools" },
  { key: "dizzy", label: "dizziness" },
  { key: "swelling", label: "swollen hands or feet" },
  { key: "aches", label: "aches" },
  { key: "fog", label: "brain fog" },
  { key: "hotflush", label: "hot flushes" },
  { key: SOAKED, label: "Soaked through in under 2 h", requires: "flow:heavy" },
];

export function groupDef(id: ChoiceGroupId): ChoiceGroup {
  return CHOICE_GROUPS.find((g) => g.id === id)!;
}

/** Every key a group can hold. */
export function groupKeys(id: ChoiceGroupId): string[] {
  return groupDef(id).options.map((o) => o.key);
}

/** Every key the vocabulary knows, old and new — what "Feeling fine" takes back. */
export function allSymptomKeys(): string[] {
  return [
    ...LEGACY_KEYS,
    ...CHOICE_GROUPS.flatMap((g) => g.options.map((o) => o.key)),
    ...TOGGLES.map((t) => t.key),
  ];
}

/**
 * The day's live keys. `setSymptom` keeps `symptoms` free of taken-back keys, but a blob merged
 * by an older build may not have, so a key whose last removal out-stamps its last add is dropped
 * here too — the same comparison the merge makes.
 */
export function symptomsOf(S: KayaState, ds: string): string[] {
  const L = S.logs[ds];
  if (!L || !L.symptoms) return [];
  const at = L.symptomsAt || {};
  const off = L.symptomsOff || {};
  return L.symptoms.filter((k) => (at[k] ?? 1) > (off[k] ?? 0));
}

/** The old keys the new ones stand for, so every existing `cramps` tally keeps counting. */
export function implies(keys: string[]): string[] {
  const out = new Set(keys);
  if (keys.includes("flow:heavy")) out.add("heavy");
  if (keys.some((k) => k === "pain:1" || k === "pain:2" || k === "pain:3")) out.add("cramps");
  if (keys.includes("energy:0")) out.add("lowenergy");
  if (keys.some((k) => k === "breast:1" || k === "breast:2" || k === "breast:3")) out.add("tender");
  return [...out];
}

/** The legacy key a group's value implies, if any. */
export function impliedBy(group: ChoiceGroupId, value: string | null): string | null {
  if (group === "flow") return value === "heavy" ? "heavy" : null;
  if (group === "pain") return value && value !== "0" ? "cramps" : null;
  if (group === "energy") return value === "0" ? "lowenergy" : null;
  if (group === "breast") return value && value !== "0" ? "tender" : null;
  return null;
}

/** The legacy key a group can imply at all. */
export function legacyOf(group: ChoiceGroupId): string | null {
  const legacy: Partial<Record<ChoiceGroupId, string>> = {
    flow: "heavy",
    pain: "cramps",
    breast: "tender",
    energy: "lowenergy",
  };
  return legacy[group] ?? null;
}

export function has(keys: string[], key: string): boolean {
  return implies(keys).includes(key);
}

/**
 * The value chosen in a group, or null. An old day that holds only `heavy` or `lowenergy` answers
 * for flow and energy; `cramps` alone says there was pain but not how much, so it does not.
 */
export function choice(keys: string[], g: ChoiceGroupId): string | null {
  const prefix = `${g}:`;
  // the last one written wins, should a merge by an old build ever leave two
  for (let i = keys.length - 1; i >= 0; i--) if (keys[i].startsWith(prefix)) return keys[i].slice(prefix.length);
  if (g === "flow" && keys.includes("heavy")) return "heavy";
  if (g === "energy" && keys.includes("lowenergy")) return "0";
  return null;
}

/** Any bleeding logged that day, old chip or new. */
export function hasFlow(keys: string[]): boolean {
  return choice(keys, "flow") !== null;
}

/** How strongly a row of the symptom map shows on a day, 0..3; null when the row says nothing. */
export function intensity(keys: string[], row: SymptomRowId): number | null {
  switch (row) {
    case "flow": {
      const v = choice(keys, "flow");
      return v === null ? null : ({ spotting: 0.5, light: 1, medium: 2, heavy: 3 } as Record<string, number>)[v] ?? null;
    }
    case "pain": {
      const v = choice(keys, "pain");
      if (v !== null) return Number(v);
      return keys.includes("cramps") ? 2 : null;
    }
    case "energy": {
      // the map shades low energy, so the scale runs the other way
      const v = choice(keys, "energy");
      return v === null ? null : ({ "0": 3, "1": 1, "2": 0 } as Record<string, number>)[v] ?? null;
    }
    case "mood": {
      const v = choice(keys, "mood");
      return v === null ? null : ({ steady: 0, low: 2, irritable: 3, anxious: 3 } as Record<string, number>)[v] ?? null;
    }
    case "sleep": {
      const v = choice(keys, "sleep");
      return v === null ? null : ({ "0": 3, "1": 1, "2": 0 } as Record<string, number>)[v] ?? null;
    }
    case "tender": {
      // an old day's toggle said there was breast pain but not how much, as `cramps` does
      const v = choice(keys, "breast");
      if (v !== null) return Number(v);
      return keys.includes("tender") ? 2 : null;
    }
    case "digestion":
      return keys.includes("constipation") || keys.includes("loose") ? 3 : null;
    case "bloating":
    case "headache":
    case "backache":
    case "nausea":
    case "skin":
    case "dizzy":
    case "swelling":
    case "aches":
    case "fog":
    case "hotflush":
      return keys.includes(row) ? 3 : null;
    case "cravings":
      return keys.includes("cravings:sweet") || keys.includes("cravings:salty") ? 3 : null;
  }
}

export type SymptomRowId =
  | "flow"
  | "pain"
  | "energy"
  | "bloating"
  | "cravings"
  | "mood"
  | "sleep"
  | "headache"
  | "tender"
  | "backache"
  | "nausea"
  | "skin"
  | "digestion"
  | "dizzy"
  | "swelling"
  | "aches"
  | "fog"
  | "hotflush";

/** The symptom map's rows, in the artboard's order. */
export const SYMPTOM_ROWS: { id: SymptomRowId; label: string }[] = [
  { id: "flow", label: "flow" },
  { id: "pain", label: "pain" },
  { id: "energy", label: "energy" },
  { id: "bloating", label: "bloating" },
  { id: "cravings", label: "cravings" },
  { id: "mood", label: "mood" },
  { id: "sleep", label: "sleep" },
  { id: "headache", label: "headache" },
  { id: "tender", label: "breast pain" },
  { id: "backache", label: "back ache" },
  { id: "nausea", label: "nausea" },
  { id: "skin", label: "skin" },
  { id: "digestion", label: "digestion" },
  { id: "dizzy", label: "dizziness" },
  { id: "swelling", label: "swelling" },
  { id: "aches", label: "aches" },
  { id: "fog", label: "brain fog" },
  { id: "hotflush", label: "hot flushes" },
];

const FLOW_WORD: Record<string, string> = {
  spotting: "spotting",
  light: "light flow",
  medium: "medium flow",
  heavy: "heavy flow",
};
const GRADE_WORD: Record<string, string> = { "1": "mild", "2": "moderate", "3": "severe" };
const TOGGLE_WORD: Record<string, string> = { "cravings:sweet": "sweet cravings", "cravings:salty": "salty cravings" };

/**
 * A day's log in words, in a fixed order — the symptom diary's line and its rows' subtitles
 * (ADR-060). Only what was logged is said, and a scale's ordinary answer (no pain, okay energy,
 * steady mood) says nothing. `cycleTraining.ts::loggedWords` is the session line's shorter list
 * and stays separate.
 */
export function dayWords(keys: string[]): string[] {
  const out: string[] = [];
  const flow = choice(keys, "flow");
  if (flow !== null && FLOW_WORD[flow]) out.push(FLOW_WORD[flow]);
  if (flow === "heavy" && keys.includes(SOAKED)) out.push("soaked through");
  const pain = choice(keys, "pain");
  if (pain !== null) {
    if (GRADE_WORD[pain]) out.push(`${GRADE_WORD[pain]} cramps`);
  } else if (keys.includes("cramps")) out.push("cramps");
  const breast = choice(keys, "breast");
  if (breast !== null) {
    if (GRADE_WORD[breast]) out.push(`${GRADE_WORD[breast]} breast pain`);
  } else if (keys.includes("tender")) out.push("tender breasts");
  if (choice(keys, "energy") === "0") out.push("low energy");
  for (const t of TOGGLES) if (t.key !== SOAKED && keys.includes(t.key)) out.push(TOGGLE_WORD[t.key] ?? t.label);
  const mood = choice(keys, "mood");
  if (mood === "low") out.push("low mood");
  else if (mood === "irritable" || mood === "anxious") out.push(mood);
  if (choice(keys, "sleep") === "0") out.push("poor sleep");
  if (!out.length && keys.includes(SYMPTOM_FINE)) return ["feeling fine"];
  return out;
}
